import '../css/PanelRunsLinePlot.less';

import * as _ from 'lodash';
import classNames from 'classnames';

import memoize from 'memoize-one';
import * as React from 'react';
import {useCallback, useContext} from 'react';
import {Button, Loader, Popup, Tab, Checkbox} from 'semantic-ui-react';
import {PanelConfigSpecToProps} from './property-editors/property-editors';
import {RunQueryContext, RunsQueryContext} from '../state/runs/context';
import {useRunsData} from '../state/runs/hooks';
import LegacyWBIcon from './elements/LegacyWBIcon';
import ModifiedDropdown from './elements/ModifiedDropdown';
import PanelTitle from './elements/PanelTitle';
import RangeInput from './elements/RangeInput';
import MetricsPicker from './elements/MetricsPicker';
import WandbLoader from './WandbLoader';
import PlotWarning from './PlotWarning';
import SmoothingInput from './elements/SmoothingInput';

import {
  QueryToRunsDataQueryParams,
  RunsDataQuery,
  toRunsDataQuery,
} from '../containers/RunsDataLoader';
import {CHART_SAMPLES} from '../util/constants';
import {Expression, metricsInExpression} from '../util/expr';
import * as Filters from '../util/filters';
import * as Panels from '../util/panels';
import {
  Line,
  Mark,
  Timestep,
  PlotType,
  prettyXAxisLabel,
  getLinesFromData,
  AggregateCalculation,
  overrideLineColors,
  overrideLineTitles,
  overrideMarks,
  convertLineToBar,
} from '../util/plotHelpers';

import {SmoothingType} from '../util/math';

import {
  legendTemplateFieldNames,
  getDefaultLegendTemplate,
} from '../util/legend';
import * as Query from '../util/queryts';
import * as RunHelpers from '../util/runhelpers';
import * as Run from '../util/runs';
import {
  RunHistoryKeyInfo,
  RunHistoryRow,
  RunHistoryKeyType,
} from '../types/run';

import PanelError from './elements/PanelError';
import LinePlot from './vis/LinePlot';
import docUrl from '../util/doc_urls';
import {useGatedValue} from '../state/hooks';
import LabeledOption from './elements/LabeledOption';
import PanelGroupingOptions from './PanelGroupingOptions';
import PanelLegend from './PanelLegend';
import PanelChartOptions from './PanelChartOptions';
import PanelExpressionOptions, {
  parseExpressions,
  getExpressionFields,
} from './PanelExpressionOptions';
import BarChart from './vis/BarChart';

import {useSampleAndQueryToTable} from '../components/Export';

import makeComp from '../util/profiler';

export const PANEL_TYPE = 'Run History Line Plot';
const DEFAULT_MAX_GROUP_RUNS = 100;
const ABSOLUTE_MAX_GROUP_RUNS = 1000;

// maximum metrics to get when using regex to pick metrics
const REGEX_METRIC_MAX_LEN = 10;

export const X_AXIS_LABELS: {[key: string]: string} = {
  _step: 'Step',
  _absolute_runtime: 'Relative Time (Wall)',
  _runtime: 'Relative Time (Process)',
  _timestamp: 'Wall Time',
};

export interface RunsLinePlotConfig {
  xLogScale?: boolean;
  yLogScale?: boolean;
  xAxis?: string;
  startingXAxis?: string;
  smoothingWeight?: number;
  smoothingType?: SmoothingType;
  useGlobalSmoothingWeight?: boolean;
  ignoreOutliers?: boolean;
  showOriginalAfterSmoothing?: boolean; // Show the line and the smoothed line
  xAxisMin?: number;
  xAxisMax?: number;
  yAxisMin?: number;
  yAxisMax?: number;
  legendFields?: string[];
  legendTemplate?: string; // used to generate the default legend
  aggregate?: boolean; // user selected grouping
  aggregateMetrics?: boolean; // aggregate metrics into single metric
  groupBy?: string; // key to group by
  metrics?: string[]; // names of yAxis metrics
  metricRegex?: string; // can choose yAxis metrics by regex
  useMetricRegex?: boolean;
  yAxisAutoRange?: boolean;
  chartTitle?: string;
  xAxisTitle?: string;
  yAxisTitle?: string;
  limit?: number; // max number of runs or groups to show
  groupRunsLimit?: number;
  expressions?: string[];
  xExpression?: string; // expression for x axis
  plotType?: PlotType;
  groupAgg?: 'mean' | 'min' | 'max' | 'median';
  groupArea?: 'minmax' | 'stddev' | 'stderr' | 'none' | 'samples';
  colorEachMetricDifferently?: boolean; // if we have multiple metrics, override the run colors
  overrideSeriesTitles?: {[key: string]: string}; // For setting the specific names of lines
  overrideColors?: {[key: string]: {color: string; transparentColor: string}};
  overrideMarks?: {[key: string]: Mark};
  showLegend?: boolean; // Display legend in chart (default true)
}
type RunsLinePlotPanelProps = Panels.PanelProps<RunsLinePlotConfig>;

interface Range {
  min: number | null;
  max: number | null;
}

const getRunSets = (query: Query.Query) => {
  return query.runSets != null
    ? query.runSets.map(rs => ({
        id: rs.id,
        grouping: rs.grouping,
        entityName: rs.entityName,
        projectName: rs.projectName,
      }))
    : [];
};

const runsLinePlotTransformQuery = (
  query: Query.Query,
  config: RunsLinePlotConfig,
  xStepRange: Range | null,
  parsedExpressions: {
    expressions: Expression[] | undefined;
    xExpression: Expression | undefined;
  }
): RunsDataQuery => {
  let singleRun = false;
  if (query.runName) {
    singleRun = true;
  }

  let result;
  if (singleRun) {
    result = toRunsDataQuery(query);
  } else {
    const queryToDataQueryParams: QueryToRunsDataQueryParams = {
      selectionsAsFilters: true,
    };

    if (config.metrics != null && config.metrics.length > 0) {
      const filters: Filters.Filter[] = config.metrics.map(metric => ({
        key: {section: 'keys_info', name: metric},
        op: '!=',
        value: null,
      }));
      const mergeFilters: Filters.Filter = Filters.Or(filters);
      queryToDataQueryParams.mergeFilters = mergeFilters;
    }
    result = toRunsDataQuery(query, queryToDataQueryParams);
  }

  result.disabled = true;

  let legendFields = config.legendFields || [];
  if (config.legendTemplate != null) {
    const templateFields = legendTemplateFieldNames(config.legendTemplate);
    const extraLegendFields = _.difference(
      templateFields,
      config.legendFields || []
    );
    legendFields = legendFields.concat(extraLegendFields);
  }

  let displayFields = [
    ...legendFields.map(Run.keyFromString),
    ...(query.grouping || []),
    config.aggregate && config.groupBy
      ? Run.key('config', config.groupBy)
      : null,
  ];

  const expressionFields = getExpressionFields(parsedExpressions);

  // We need to query grouping values for runsets if the runsets have grouping
  // since we do the grouping locally.
  if (query.runSets != null) {
    query.runSets.forEach((rs: any) => {
      if (rs.grouping) {
        displayFields = displayFields.concat(rs.grouping);
      }
    });
  }

  const extraFields = _.uniq(_.concat(displayFields, expressionFields));

  result.configKeys = extraFields
    .filter(key => key && key.section === 'config')
    .map(key => key!.name);
  result.summaryKeys = extraFields
    .filter(key => key && key.section === 'summary')
    .map(key => key!.name);

  if (config.xAxis != null && config.metrics != null) {
    // We load the history data for the graphs
    // We sample values where an individual metric and the xAxis has values
    // Some users might prefer to sample history rows where all of the metrics
    // and the xAxis has a value, but this will cause some graphs to not display
    // if for example a user logs test data in one step and training data in another
    // and then wants to plot the two metrics in a single graph.
    result.disabled = false;
    result.history = true;

    const derivedXAxis = getDerivedXAxis(config);

    const hasSystemMetrics = getHasSystemMetrics(config);

    let xAxisHistoryKeys: string[] = []; // history keys we need besides the metric

    if (!hasSystemMetrics) {
      xAxisHistoryKeys.push('_step');
    }
    if (derivedXAxis != null) {
      if (derivedXAxis === '_absolute_runtime') {
        // we calculate absolute wall time from the start of the run
        // we need both timestamp and runtime to do this
        xAxisHistoryKeys.push('_timestamp');
        xAxisHistoryKeys.push('_runtime');
      } else {
        // this is the normal case
        xAxisHistoryKeys.push(derivedXAxis);
      }
    }

    const yAxisExprKeys = _.flatten(
      (parsedExpressions.expressions || []).map(expr =>
        metricsInExpression(expr)
      )
    );
    const xAxisExprKeys =
      parsedExpressions.xExpression != null
        ? metricsInExpression(parsedExpressions.xExpression)
        : [];

    xAxisHistoryKeys = xAxisHistoryKeys.concat(xAxisExprKeys);

    result.historySpecs = _.uniq(
      _.concat(config.metrics, yAxisExprKeys, xAxisExprKeys)
    ).map(metricKey => ({
      keys: _.uniq(_.concat(xAxisHistoryKeys, metricKey)),
      samples: CHART_SAMPLES,
      ...(xStepRange != null
        ? {
            minStep: xStepRange.min,
            maxStep: xStepRange.max,
          }
        : {}),
    }));

    result.page = {
      size: singleRun ? 1 : config.limit || 10,
    };
    if (Panels.isGrouped(query, config)) {
      // We need the metadata for grouping because we do it locally
      // TODO: move grouping to server
      // result.disableMeta = false;

      // optionally compute group statistics over all runs instead of sub-sampling
      result.page.size = config.groupRunsLimit || DEFAULT_MAX_GROUP_RUNS;
      // Disable grouping for this query, we'll do it ourselves.
      result.queries = result.queries.map(q => ({...q, grouping: []}));
    }
  }
  return result;
};

const getSelectedMetrics = (
  metricRegex: string,
  keyInfo?: RunHistoryKeyInfo
) => {
  let regex: RegExp | undefined;
  try {
    regex = new RegExp(metricRegex, 'g');
  } catch {
    return [];
  }
  if (regex == null || keyInfo == null) {
    return [];
  }

  const keys = _.keys(keyInfo.keys).filter(k => (regex as RegExp).test(k));
  keys.sort();
  return keys.slice(0, REGEX_METRIC_MAX_LEN);
};

// Max number of lines
const limit = (props: RunsLinePlotPanelProps, config: RunsLinePlotConfig) => {
  if (Panels.isSingleRun(props)) {
    return 10; // a single run might have multiple metrics or expressions
  } else {
    return config.limit || 10;
  }
};

const warnMaxPlotLinesShowingLimit = (maxRuns: number, plotUnit: string) => {
  return (
    <PlotWarning
      helpText={
        'Click edit (top right corner) to change the max number of ' +
        plotUnit +
        ' displayed'
      }
      message={'Showing first ' + maxRuns + ' ' + plotUnit}
    />
  );
};

const warnRunSamplingWhenGroupedLimit = (maxRunsSampled: number) => {
  return (
    <PlotWarning
      helpText={
        'Click edit (top right corner) to change the number of runs sampled in group metrics'
      }
      message={'Computing group metrics from first ' + maxRunsSampled + ' runs'}
    />
  );
};

const warnHistogramMultiRun = (lines: Line[]) => {
  const firstRun = lines[0].run;

  return (
    <PlotWarning
      message={
        'Showing histgram for ' + firstRun?.displayName ?? 'the first run'
      }
    />
  );
};

const calcStepRange = (
  data: Array<{
    keyInfo: RunHistoryKeyInfo;
    name: string;
    displayName: string;
    history: RunHistoryRow[];
  }>,
  xAxis?: string,
  xMin?: number,
  xMax?: number,
  xExpression?: string
) => {
  // figure out the minstep and maxstep for querying
  const extraLeftMargin = 50; // extra margin for smoothing
  const key = xAxis;
  if (key == null) {
    return [null, null];
  }
  if (xExpression != null) {
    return [null, null];
  }
  let xMins: number[] = [];
  let xMaxs: number[] = [];

  if (xMin != null) {
    // find the step of the record with largest x value less than xmin
    xMins = data
      .map(d => {
        let runMinStep: number = -Infinity;

        for (const runHistoryRow of d.history) {
          if (runHistoryRow[key] && typeof runHistoryRow[key] === 'number') {
            if (runHistoryRow[key] > xMin) {
              break;
            }
            if (
              runHistoryRow._step &&
              typeof (runHistoryRow._step === 'number')
            ) {
              if (runHistoryRow._step > runMinStep) {
                runMinStep = runHistoryRow._step;
              }
            }
          }
        }
        return runMinStep;
      })
      .filter(d => isFinite(d));
  }
  if (xMax != null) {
    // find the step of the record with smallest x value more than xmax
    xMaxs = data
      .map(d => {
        let runMaxStep: number = Infinity;

        for (let i = d.history.length - 1; i >= 0; i--) {
          const runHistoryRow = d.history[i];
          if (runHistoryRow[key] && typeof runHistoryRow[key] === 'number') {
            if (runHistoryRow[key] < xMax) {
              if (xAxis !== '_step' && runMaxStep === Infinity) {
                // handle the unusual case of run finishing inside the zoom range
                // but having the highest step.
                // this logic cleans up when we can set an xmin and xmax (non-step)
                if (runHistoryRow._step) {
                  if (xMin == null || runHistoryRow[key] > xMin) {
                    if (
                      runHistoryRow._step &&
                      typeof (runHistoryRow._step === 'number')
                    ) {
                      runMaxStep = runHistoryRow._step;
                    }
                  }
                }
              }
              break;
            }
            if (
              runHistoryRow._step &&
              typeof (runHistoryRow._step === 'number')
            ) {
              if (runHistoryRow._step < runMaxStep) {
                runMaxStep = runHistoryRow._step;
              }
            }
          }
        }
        return runMaxStep;
      })
      .filter(d => isFinite(d));
  }

  let minStep: number | null;
  let maxStep: number | null;

  if (xAxis === '_step') {
    // if xAxis is step we can choose the narrowest range
    maxStep = _.min(xMaxs) || null;
    minStep = _.max(xMins) || null;
  } else {
    // since we don't know if the steps corresponding to the xranges overlap
    // we need to choose the widest range
    maxStep = _.max(xMaxs) || null;
    minStep = _.min(xMins) || null;
  }

  if (minStep != null) {
    minStep = minStep - 1 - extraLeftMargin;
  }

  if (maxStep != null) {
    // We want results <= max step so add one
    maxStep = maxStep + 2;
  }
  return [minStep, maxStep];
};

const isHistogramPlot = (
  config: Pick<RunsLinePlotConfig, 'metrics'>,
  keyInfo?: RunHistoryKeyInfo
) => {
  // Return true if currently plotting a histogram

  if (keyInfo == null) {
    return false;
  }
  const keyTypes = RunHelpers.keyTypes(keyInfo);
  if (keyTypes && config.metrics != null && config.metrics.length > 0) {
    if (keyTypes[config.metrics[0]] === 'histogram') {
      return true;
    }
  }
  return false;
};

const isBarPlot = (props: RunsLinePlotPanelInnerProps) => {
  // When theres more than one line and all the visible lines are a single point
  // we show a bar plot.
  const {lines, zooming} = props;
  const filteredLines = lines.filter(l => !l.aux && !(l.data.length === 0));
  if (
    filteredLines.length > 1 &&
    _.every(filteredLines, line => line.data.length === 1) &&
    !zooming
  ) {
    return true;
  } else {
    return false;
  }
};

const isEmptyChart = (
  config: Pick<RunsLinePlotConfig, 'metrics' | 'expressions'>
) => {
  return (
    (config.metrics == null || config.metrics.length === 0) &&
    (config.expressions == null || config.expressions.length === 0)
  );
};

const renderBarPlot = (props: RunsLinePlotPanelInnerProps) => {
  const {lines, config} = props;
  const filteredLines = lines.filter(l => !l.aux && !(l.data.length === 0));

  // convert lines to bars
  const bars = filteredLines.map(l => convertLineToBar(l));

  return (
    <BarChart
      bars={bars}
      min={config.yAxisMin}
      max={config.yAxisMax}
      showAllLabels={true}
    />
  );
};

const defaultTitle = (
  config: Pick<RunsLinePlotConfig, 'metrics' | 'expressions'>
) => {
  const metricsInUse =
    config.expressions != null && config.expressions[0] !== ''
      ? config.expressions
      : config.metrics;

  return metricsInUse != null ? metricsInUse.join(', ') : '';
};

type RunsLinePlotPanelInnerProps = RunsLinePlotPanelProps & {
  lines: Line[];
  lineCount: number;
  defaultLegendTemplate: string;
  setQueryStepRange: React.Dispatch<React.SetStateAction<Range>>;
  setZoomTimestep: React.Dispatch<
    React.SetStateAction<'seconds' | 'minutes' | 'hours' | 'days' | null>
  >;
  zooming: boolean;
  setZooming: React.Dispatch<React.SetStateAction<boolean>>;
};

const getHasSystemMetrics = (config: Pick<RunsLinePlotConfig, 'metrics'>) =>
  config.metrics != null &&
  config.metrics.find(metric => metric.startsWith('system/'));

// system metrics charts can't have _step or null xAxes, so for those
// charts we fall back to _runtime as the default
const getDerivedXAxis = (
  config: Pick<RunsLinePlotConfig, 'metrics' | 'xAxis'>
) => {
  if (getHasSystemMetrics(config)) {
    // system metrics have no '_step' to log by, so we use '_runtime'
    // as the default instead
    if (config.xAxis == null || config.xAxis === '_step') {
      return '_runtime';
    }

    return config.xAxis;
  }

  // all non system-metrics charts should use '_step' as the default
  if (config.xAxis == null) {
    return '_step';
  }

  return config.xAxis;
};

const getXAxisChoices = (
  config: RunsLinePlotConfig,
  keyInfo: RunHistoryKeyInfo,
  keyTypes: {
    [key: string]: RunHistoryKeyType;
  }
) => {
  let xAxisChoices: string[] = [];

  // Allow choosing any xAxis value that was logged along with any metric.
  if (config.metrics != null && config.metrics.length > 0) {
    xAxisChoices = _.flatten(
      config.metrics.map(metric => {
        return RunHelpers.keysInCommon(keyInfo, metric);
      })
    );
  } else {
    xAxisChoices = _.keys(keyInfo.keys);
  }

  // Limit xAxis choices to number types
  // Put the monotonic xAxis choices first
  // Don't show non-monotic choices if we're looking at a histogram
  const xAxisFiltered = xAxisChoices.filter(k => keyTypes[k] === 'number');
  const xAxisMonotonicChoices = xAxisFiltered.filter(
    k => keyInfo.keys[k].monotonic
  );
  const xAxisNotMonotonicChoices = xAxisFiltered.filter(
    k => !keyInfo.keys[k].monotonic
  );

  xAxisChoices = _.uniq(
    [
      // _step is not available as an option for system metrics charts
      ...(getHasSystemMetrics(config) ? [] : ['_step']),
      '_absolute_runtime',
      '_runtime',
      '_timestamp',
    ].concat(
      xAxisMonotonicChoices,
      isHistogramPlot(config, keyInfo) ? [] : xAxisNotMonotonicChoices
    )
  );
  const derivedXAxis = getDerivedXAxis(config);
  if (!_.includes(xAxisChoices, derivedXAxis)) {
    xAxisChoices.push(derivedXAxis);
  }
  return xAxisChoices;
};

const getYAxisChoices = (
  config: RunsLinePlotConfig,
  keyInfo: RunHistoryKeyInfo,
  keyTypes: {
    [key: string]: RunHistoryKeyType;
  },
  runsContext: Partial<RunsQueryContext>
) => {
  let xAxis = getDerivedXAxis(config);
  // absolute runtime is a special derived xAxis that is calculated post-run
  if (xAxis === '_absolute_runtime') {
    xAxis = '_runtime';
  }

  let yAxisChoices = RunHelpers.keysInCommon(keyInfo, xAxis);

  yAxisChoices = _.filter(
    yAxisChoices,
    k =>
      (keyTypes[k] === 'number' &&
        !['_absolute_runtime', '_runtime', '_step', '_timestamp'].includes(
          k
        )) ||
      (keyTypes[k] === 'histogram' &&
        // we disallow selecting histograms on multi-run pages (excluding reports)
        (runsContext.runId != null || runsContext.report != null))
  );

  if (config.metrics != null) {
    config.metrics.forEach(metric => {
      if (!_.includes(yAxisChoices, metric)) {
        /* We have a value for the yAxis that is not in yAxisChoices
           This happens if the user configures the plot, and then changes the query
           For example on a runs page, visualizes a single run that has one set of metrics,
           adds a plot, then switch the eyeball to a run with different metrics.
          */
        yAxisChoices.push(metric);
      }
    });
  }
  return yAxisChoices;
};

const renderPlotStackType = (
  config: RunsLinePlotConfig,
  updateConfig: (newConfig: RunsLinePlotConfig) => void,
  usingExpressions: boolean
) => {
  return (
    <LabeledOption
      label="Chart Type"
      helpText="Switch between line plot, area plot, and percentage area"
      docUrl={docUrl.compareMetrics + '#plot-style'}
      option={
        <Button.Group>
          <Popup
            inverted
            size="mini"
            content="Line plot"
            trigger={
              <Button
                size="tiny"
                className={classNames('wb-icon-button', 'only-icon', {
                  'action-button--active':
                    config.plotType === 'line' || config.plotType == null,
                })}
                onClick={() => {
                  updateConfig({plotType: 'line'});
                }}>
                <LegacyWBIcon name="standard-line-plot" />
              </Button>
            }
          />
          <Popup
            inverted
            size="mini"
            content="Area plot"
            trigger={
              <Button
                size="tiny"
                className={classNames('wb-icon-button', 'only-icon', {
                  'action-button--active': config.plotType === 'stacked-area',
                })}
                onClick={() => {
                  updateConfig({plotType: 'stacked-area'});
                }}>
                <LegacyWBIcon name="area-line-plot" />
              </Button>
            }
          />
          <Popup
            inverted
            size="mini"
            content="Percent area plot"
            trigger={
              <Button
                size="tiny"
                className={classNames('wb-icon-button', 'only-icon', {
                  'action-button--active': config.plotType === 'pct-area',
                })}
                onClick={() => {
                  updateConfig({plotType: 'pct-area'});
                }}>
                <LegacyWBIcon name="percent-line-plot" />
              </Button>
            }
          />
        </Button.Group>
      }
    />
  );
};

const Config = makeComp(
  (props: RunsLinePlotPanelInnerProps) => {
    const {config, updateConfig} = props;
    const runsContext = useContext(RunQueryContext);

    const singleRun = Panels.isSingleRun(props);
    const keyInfo = props.keyInfo!;
    if (!props.data.histories) {
      return <p>No Histories</p>;
    }

    const keyTypes = RunHelpers.keyTypes(keyInfo);

    const yAxisChoices = getYAxisChoices(
      config,
      keyInfo,
      keyTypes,
      runsContext
    );
    const xAxisChoices = getXAxisChoices(config, keyInfo, keyTypes);

    const xAxisOptions = xAxisChoices.map(k => ({
      icon:
        k === '_runtime' || k === '_timestamp' || k === '_absolute_runtime'
          ? 'calendar'
          : keyInfo.keys[k] && !keyInfo.keys[k].monotonic
          ? 'chart bar'
          : 'wbic-ic-up-arrow',
      text:
        (X_AXIS_LABELS[k] || k) +
        (keyInfo.keys[k] && !keyInfo.keys[k].monotonic
          ? ' (Not Monotonically Increasing)'
          : ''),
      value: k,
      key: k,
    }));

    const usingExpressions =
      config.expressions != null &&
      config.expressions.length !== 0 &&
      !(config.expressions.length === 1 && config.expressions[0] === '');

    // if we're using grouping we should say things like limit 10 groups
    // if not we should say limit 10 runs
    // This doesn't handle the case where there are groups and runs - it
    // just calls them groups.
    const displayGroupsOrRunsStr = Panels.isGrouped(props.pageQuery, config)
      ? 'group'
      : 'run';

    const dataTab = (
      <Tab.Pane as="div" className="form-grid">
        {isBarPlot(props) && (
          <p className="hint-text">
            Showing a bar chart instead of a line chart because all logged
            values are length one.
          </p>
        )}
        <LabeledOption
          label="X"
          helpText="Use step, time or any variable logged with wandb.log as xaxis."
          docUrl={docUrl.compareMetrics + '#x-axis'}
          option={
            <div style={{display: 'flex'}}>
              <ModifiedDropdown
                lazyLoad
                style={{flexGrow: 1}}
                placeholder="X Axis"
                search
                selection
                options={xAxisOptions}
                value={getDerivedXAxis(config)}
                onChange={(e, {value}) => {
                  props.setQueryStepRange({min: null, max: null});
                  updateConfig({
                    xAxis: value as string,
                  });
                }}
              />
            </div>
          }
        />
        <LabeledOption
          label="Y"
          helpText="Plot any numeric metric logged with wandb.log."
          docUrl={docUrl.compareMetrics + '#y-axis-variables'}
          option={
            <div style={{display: 'flex'}}>
              <MetricsPicker
                value={config.metrics}
                regexValue={config.metricRegex}
                options={yAxisChoices}
                keyTypes={keyTypes}
                regexInput={config.useMetricRegex ?? false}
                onRegexChange={(newRegex: string) => {
                  updateConfig({
                    metrics: getSelectedMetrics(newRegex, props.keyInfo),
                    metricRegex: newRegex,
                    useMetricRegex: true,
                  });
                }}
                onMetricsChange={(newMetrics: string[]) => {
                  // If we're add a histogram make it the only metric.
                  if (
                    newMetrics.some(k => keyTypes[k] === 'histogram') &&
                    newMetrics.length > 1
                  ) {
                    newMetrics = [_.last(newMetrics)!];
                  }
                  updateConfig({
                    metrics: newMetrics,
                    useMetricRegex: false,
                  });
                }}
              />
            </div>
          }
        />
        <LabeledOption
          label="X Axis"
          helpText="Fix the minimum and maximum values for the x-axis"
          docUrl={docUrl.compareMetrics + '#x-range-and-y-range'}
          option={
            <RangeInput
              disabled={isEmptyChart(config) || isBarPlot(props)}
              onMinChange={newVal => {
                // reset the query step range so that it can be recalculated on all of the data
                props.setQueryStepRange({min: null, max: null});
                updateConfig({xAxisMin: newVal});
              }}
              onMaxChange={newVal => {
                // reset the query step range so that it can be recalculated on all of the data
                props.setQueryStepRange({min: null, max: null});
                updateConfig({xAxisMax: newVal});
              }}
              minValue={config.xAxisMin}
              maxValue={config.xAxisMax}
              log
              logValue={config.xLogScale}
              onLogChange={newVal => {
                updateConfig({
                  xLogScale: newVal || false,
                });
              }}
            />
          }
        />

        <LabeledOption
          label="Y Axis"
          helpText="Fix the minimum and maximum values for the y-axis"
          docUrl={docUrl.compareMetrics + '#x-range-and-y-range'}
          option={
            <>
              <RangeInput
                disabled={isEmptyChart(config)}
                onMinChange={newVal => {
                  updateConfig({yAxisMin: newVal});
                }}
                onMaxChange={newVal => {
                  updateConfig({yAxisMax: newVal});
                }}
                minValue={config.yAxisMin}
                maxValue={config.yAxisMax}
                log
                logValue={config.yLogScale ?? false}
                onLogChange={newVal =>
                  updateConfig({
                    yLogScale: newVal ?? false,
                  })
                }
                ignoreOutliers
                ignoreOutliersValue={config.ignoreOutliers ?? false}
                onIgnoreOutliersChange={newVal =>
                  updateConfig({
                    ignoreOutliers: newVal ?? false,
                  })
                }
              />
            </>
          }
        />

        <LabeledOption
          label={'Smoothing'}
          helpText="Choose strategy for smoothing lines"
          docUrl={docUrl.compareMetrics + '#smoothing'}
          option={
            <div style={{display: 'flex'}}>
              <SmoothingInput
                smoothingParam={config.smoothingWeight}
                smoothingType={config.smoothingType}
                smoothingTypeDropdown
                onChange={(newSmoothingParam, newSmoothingType) => {
                  updateConfig({
                    smoothingWeight: newSmoothingParam,
                    smoothingType: newSmoothingType,
                    useGlobalSmoothingWeight: false,
                  });
                }}
              />
            </div>
          }
        />
        {config.smoothingWeight !== 0 &&
          !Panels.isGrouped(props.pageQuery, config) && (
            <LabeledOption
              label={'Show Original'}
              helpText="Show lines before smoothing"
              option={
                <Checkbox
                  toggle
                  checked={config.showOriginalAfterSmoothing ?? true}
                  name="aggregate"
                  onChange={(e, value) =>
                    updateConfig({
                      showOriginalAfterSmoothing: value.checked,
                    })
                  }
                />
              }
            />
          )}
        {!singleRun && (
          <LabeledOption
            label={`Max ${displayGroupsOrRunsStr}s to show`}
            helpText="Maximum number of runs or groups to plot"
            docUrl={docUrl.compareMetrics + '#max-runs-groups'}
            option={
              <ModifiedDropdown
                lazyLoad
                className="no-wrap-options"
                value={limit(props, config)}
                style={{minWidth: 60}}
                selection
                options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 30, 50, 100].map(
                  n => ({
                    value: n,
                    text: `${n} ${
                      // Display "run", "runs", "group" or "groups"
                      n > 1
                        ? displayGroupsOrRunsStr + 's'
                        : displayGroupsOrRunsStr
                    }`,
                  })
                )}
                onChange={(e, {value}) => {
                  if (typeof value === 'number') {
                    updateConfig({
                      limit: value,
                    });
                  }
                }}
              />
            }
          />
        )}
        {!isBarPlot(props) &&
          (config.plotType != null ||
            !singleRun ||
            (config.metrics != null && config.metrics.length > 1)) &&
          renderPlotStackType(
            config as RunsLinePlotConfig,
            updateConfig,
            usingExpressions
          )}
      </Tab.Pane>
    );

    const groupingTab = (
      <Tab.Pane as="div" className="form-grid">
        <PanelGroupingOptions
          type={'lines'}
          config={config}
          disabled={isEmptyChart(config)}
          isGrouped={Panels.isGrouped(props.pageQuery, config) || false}
          updateConfig={updateConfig}
          defaultMaxGroupRuns={DEFAULT_MAX_GROUP_RUNS}
          absoluteMaxGroupRuns={ABSOLUTE_MAX_GROUP_RUNS}
          totalRuns={props.data.totalRuns}
          usingExpressions={usingExpressions}
          pageQuery={props.pageQuery}
          singleRun={singleRun}
        />
      </Tab.Pane>
    );
    const legendTab = (
      <Tab.Pane as="div" className="form-grid">
        <PanelLegend
          type={'lines'}
          config={config}
          updateConfig={updateConfig}
          pageQuery={props.pageQuery}
          defaultTitle={defaultTitle(config)}
          defaultXAxisTitle={prettyXAxisLabel(
            getDerivedXAxis(config) || '',
            props.lines
          )}
          editableLegendSeries={props.lines.filter(
            l => !l.aux && l.name != null
          )}
          defaultLegendTemplate={props.defaultLegendTemplate}
          singleRun={singleRun}
        />
      </Tab.Pane>
    );

    const chartTab = (
      <Tab.Pane as="div" className="form-grid">
        <PanelChartOptions
          type={'lines'}
          config={config}
          updateConfig={updateConfig}
          pageQuery={props.pageQuery}
          defaultTitle={defaultTitle(config)}
          defaultXAxisTitle={prettyXAxisLabel(
            getDerivedXAxis(config) || '',
            props.lines
          )}
        />
      </Tab.Pane>
    );

    const expressionsTab = (
      <Tab.Pane as="div" className="form-grid">
        <PanelExpressionOptions
          type={'lines'}
          config={config}
          updateConfig={updateConfig}
          availableExpressionVarNames={_.uniq(
            _.concat(xAxisChoices, yAxisChoices)
          )}
          exampleIdentifier={
            (config.metrics ? config.metrics[0] : undefined) ?? 'x'
          }
        />
      </Tab.Pane>
    );

    const settingsPanes = [
      {
        menuItem: 'Data',
        render: () => dataTab,
      },
      {
        menuItem: 'Grouping',
        render: () => groupingTab,
      },
      {
        menuItem: 'Chart',
        render: () => chartTab,
      },
      {
        menuItem: 'Legend',
        render: () => legendTab,
      },
      {
        menuItem: 'Expressions',
        render: () => expressionsTab,
      },
    ];

    return (
      <div className="chart-modal">
        <div className="chart-preview">
          <Graph {...props} />
        </div>
        <div className="chart-settings">
          <Tab
            panes={settingsPanes}
            menu={{
              secondary: true,
              pointing: true,
              className: 'chart-settings-tab-menu',
            }}
          />
        </div>
      </div>
    );
  },
  {id: 'PanelRunsLinePlot.Config'}
);

const Graph = makeComp(
  (props: RunsLinePlotPanelInnerProps) => {
    const {lines, lineCount, config, loading: reloading} = props;

    const memWarnRunSamplingWhenGroupedLimit = useCallback(
      memoize(warnRunSamplingWhenGroupedLimit),
      [warnRunSamplingWhenGroupedLimit]
    );
    const memWarnMaxPlotLinesShowingLimit = useCallback(
      memoize(warnMaxPlotLinesShowingLimit),
      [warnMaxPlotLinesShowingLimit]
    );

    const memCalcStepRange = useCallback(memoize(calcStepRange), [
      calcStepRange,
    ]);
    if (!props.data.histories) {
      // Not sure why this condition is happening.
      // TODO: fix.
      return <p>No Histories</p>;
    }

    const maxRuns = limit(props, config);

    let xDomain;
    if (config.xAxisMin != null || config.xAxisMax != null) {
      const xMin = config.xAxisMin;
      const xMax = config.xAxisMax;
      // isNan may not be necessary now that we have parseFloat
      xDomain = [!_.isNaN(xMin) ? xMin : null, !_.isNaN(xMax) ? xMax : null];
    }

    let yDomain;
    if (config.yAxisMin != null || config.yAxisMax != null) {
      const yMin = config.yAxisMin;
      const yMax = config.yAxisMax;
      yDomain = [!_.isNaN(yMin) ? yMin : null, !_.isNaN(yMax) ? yMax : null];
    }

    let errorMessage;

    if (isEmptyChart(config)) {
      errorMessage = 'Select a metric to visualize in this line chart.';
    } else if (_.isEmpty(lines)) {
      if (config.metrics != null && config.metrics.length > 0) {
        errorMessage = (
          <>
            Select runs that logged {config.metrics[0]} <br />
            to visualize data in this line chart.
          </>
        );
      } else {
        errorMessage = <>No lines to visualize</>;
      }
    } else if (lines.every(l => _.isEmpty(l.data))) {
      errorMessage = (
        <>
          There's no data for the selected runs. <br />
          Try a different X axis setting. <br />
          Current X axis: {config.xAxis}
        </>
      );
    }
    // we want to be able to show several different warnings in the legend
    // if runs are grouped:
    // - A : warn user if there are more grouped plot lines than max runs (not all groups visible in the table will show up as lines on the chart)
    // - B : more totalRuns that groupRunsLimit (not all runs visible in the table will be used to compute the group summary metrics visible on the chart)
    // if runs are not grouped:
    // - more plot lines than max runs (not all runs from the table will show up in the chart)
    // we warn about B with higher priority than A because B is actually a tighter and more annoying restriction
    // (that is, the total number of runs in a grouped section exceeds the default limit of 100 much faster/much more often--and, crucially,
    // in a less transparent way--than the total number of groups in a section exceeds the smallest default limit of 20)

    let legendPrefix;
    if (Panels.isGrouped(props.pageQuery, config)) {
      // show user warning about group subsampling when the total runs visible in the table
      // exceed the number of runs sampled to compute group metrics in the chart
      if (
        props.data.totalRuns > (config.groupRunsLimit || DEFAULT_MAX_GROUP_RUNS)
      ) {
        legendPrefix = memWarnRunSamplingWhenGroupedLimit(
          config.groupRunsLimit || DEFAULT_MAX_GROUP_RUNS
        );
        // show user warning about max groups if the total groups visible in the table exceed
        // the maximum number of runs displayed in the chart--note this happens less often and is
        // easier to notice than the previous case
      } else if (lineCount > maxRuns) {
        legendPrefix = memWarnMaxPlotLinesShowingLimit(maxRuns, 'groups');
      }
      // show user warning about plot line limit: there are more runs visible in the table than can be displayed
      // as plot lines in the chart
    } else if (props.data.totalRuns > maxRuns) {
      legendPrefix = memWarnMaxPlotLinesShowingLimit(maxRuns, 'runs');
    }

    if (isHistogramPlot(config, props.keyInfo) && props.data.totalRuns > 1) {
      legendPrefix = warnHistogramMultiRun(lines);
    }

    const showLegend = config.showLegend ?? lineCount > 1;

    const timestep =
      lines != null && lines.length > 0 ? lines[0].timestep : undefined;

    return (
      <>
        <PanelTitle
          title={getTitleFromConfig(config)}
          searchQuery={props.searchQuery}
        />

        <div className="chart-content">
          {props.data.loading && <WandbLoader />}
          {reloading && !props.data.loading && <Loader active size="small" />}
          {!props.data.loading && errorMessage != null ? (
            <PanelError message={errorMessage} />
          ) : isBarPlot(props) ? (
            renderBarPlot(props)
          ) : (
            <LinePlot
              svg={props.svg}
              entityName={props.data.entityName}
              projectName={props.data.projectName}
              xAxis={prettyXAxisLabel(getDerivedXAxis(config), props.lines)}
              yAxis={''}
              xAxisTitle={config.xAxisTitle}
              yAxisTitle={config.yAxisTitle}
              yScale={config.yLogScale ? 'log' : 'linear'}
              xScale={config.xLogScale ? 'log' : 'linear'}
              xDomain={xDomain}
              yDomain={yDomain}
              lines={lines}
              timestep={timestep}
              showLegend={showLegend}
              disableRunLinks={props.disableRunLinks}
              legendPrefix={legendPrefix}
              ignoreOutliers={config.ignoreOutliers || false}
              zooming={props.zooming}
              zoomCallback={(xAxisMin?: number, xAxisMax?: number) => {
                if (xAxisMin == null && xAxisMax == null) {
                  props.setZoomTimestep(null);
                  props.setZooming(false);
                } else {
                  props.setZoomTimestep(timestep || null);
                  props.setZooming(true);
                }
                const xStepRange = memCalcStepRange(
                  props.data.histories.data,
                  getDerivedXAxis(config),
                  xAxisMin,
                  xAxisMax,
                  config.xExpression
                );
                props.setQueryStepRange({
                  min: xStepRange[0],
                  max: xStepRange[1],
                });
              }}
            />
          )}
        </div>
      </>
    );
  },
  {id: 'PanelRunsLinePlot.Graph'}
);

export interface PanelRunsLinePlotHandle {
  focus(): void;
}

export const PanelRunsLinePlot: React.FC<RunsLinePlotPanelProps> = makeComp(
  (props: RunsLinePlotPanelProps) => {
    // xStepRange is for sample min and max step in our hisotry query
    // The calculation is a little complicated because we have different
    // xAxis but we can only query for ranges over step.
    const [queryStepRange, setQueryStepRange] = React.useState({
      min: null,
      max: null,
    } as Range);

    const [zoomTimestep, setZoomTimestep] = React.useState(
      null as Timestep | null
    );

    const [zooming, setZooming] = React.useState(false);

    const memParseExpressions = useCallback(memoize(parseExpressions), [
      props.config.expressions,
      props.config.xExpression,
    ]);

    const parsedExpressions = memParseExpressions(
      props.config.expressions,
      props.config.xExpression
    );

    const panelQuery = runsLinePlotTransformQuery(
      props.pageQuery,
      props.config,
      queryStepRange,
      parsedExpressions
    );
    const runsDataQuery = useRunsData(panelQuery);
    const {data} = runsDataQuery;

    const savedData = useGatedValue(data, d => {
      return !d.loading;
    });

    // recalculate the step offset if the xAxisMin or xAxisMax is set and the
    // queryStepRange hasn't been set.  This needs to go here because the data
    // needs to be loaded once to figure out what the step range should be.
    if (
      (props.config.xAxisMin != null || props.config.xAxisMax != null) &&
      queryStepRange.min == null &&
      queryStepRange.max == null &&
      !zooming &&
      !data.loading
    ) {
      const [min, max] = calcStepRange(
        data.histories.data,
        getDerivedXAxis(props.config),
        props.config.xAxisMin,
        props.config.xAxisMax
      );
      if (min !== queryStepRange.min || max !== queryStepRange.max) {
        setQueryStepRange({min, max});
      }
    }

    // We need to memoize this per component instance, rather than globally.
    const memGetLinesFromData = useCallback(
      memoize(getLinesFromData, (a: any, b: any) => {
        // uncomment to see why getLinesFromData is being called
        // console.log(
        //   'PanelRunsLinePlot memoize',
        //   a[0] === b[0],
        //   a[1] === b[1],
        //   _.isEqual(a[2], b[2])
        // );
        // console.log(difference(a[2], b[2]));
        // This must memoize comparison must be kept in sync with getLinesFromData.
        // Please let Shawn know if you need to change this.
        return a[0] === b[0] && a[1] === b[1] && _.isEqual(a[2], b[2]);
      }),
      [getLinesFromData]
    );

    const config = props.config;
    const singleRun = Panels.isSingleRun(props);
    const runSets = getRunSets(props.pageQuery);

    const defaultLegendTemplate = getDefaultLegendTemplate(
      singleRun,
      Panels.isGrouped(props.pageQuery, config) ?? false,
      config.smoothingWeight != null && config.smoothingWeight !== 0,
      config.legendFields ?? [],
      config.metrics ?? [],
      runSets != null && runSets.length > 1,
      config.aggregateMetrics ?? false,
      config.plotType ?? 'line'
    );

    // LB: TODO Make this come from legend spec
    const aggregateCalculations: AggregateCalculation[] = ['minmax', 'stddev'];
    if (config.groupArea === 'stderr') {
      aggregateCalculations.push('stderr');
    } else if (config.groupArea === 'samples') {
      aggregateCalculations.push('samples');
    }

    const getLinesFromDataArgs = {
      groupBy: config.groupBy ?? 'None',
      maxRuns: limit(props, config),
      smoothingParam: config.smoothingWeight ?? 0.0,
      smoothingType: config.smoothingType ?? 'exponential',
      showOriginalAfterSmoothing: config.showOriginalAfterSmoothing ?? true,
      legendFields: config.legendFields,
      customRunColors: props.customRunColors,
      // Only pass in the part of runSets that we need. getLinesFromData memoizes
      // with deep equal.
      runSets,
      xAxis: getDerivedXAxis(config),
      yAxis: config.metrics ?? [],
      yLogScale: config.yLogScale,
      panelAggregate: config.aggregate,
      expressions: parsedExpressions.expressions,
      xExpression: parsedExpressions.xExpression,
      singleRun,
      legendTemplate: singleRun
        ? defaultLegendTemplate
        : config.legendTemplate ?? defaultLegendTemplate,
      entityName: savedData.entityName,
      projectName: savedData.projectName,
      plotType: config.plotType ?? 'line',
      zoomTimestep,
      groupLine: config.groupAgg ?? 'mean',
      groupArea: config.groupArea ?? 'minmax',
      aggregateCalculations,
      colorEachMetricDifferently:
        config.colorEachMetricDifferently ?? singleRun,
      aggregateMetrics: config.aggregateMetrics ?? false,
    };

    const linesFromData = memGetLinesFromData(
      savedData.filtered,
      savedData.histories,
      getLinesFromDataArgs
    );

    let lines = linesFromData[0];
    const lineCount = linesFromData[1];

    const memOverrideColors = useCallback(memoize(overrideLineColors), [
      overrideLineColors,
    ]);

    const memOverrideLineTitles = useCallback(memoize(overrideLineTitles), [
      overrideLineTitles,
    ]);

    const memOverrideMarks = useCallback(memoize(overrideMarks), [
      overrideMarks,
    ]);

    if (config.overrideSeriesTitles != null) {
      lines = memOverrideLineTitles(
        lines,
        config.overrideSeriesTitles,
        !singleRun
      );
    }

    if (config.overrideColors != null) {
      lines = memOverrideColors(lines, config.overrideColors, !singleRun);
    }

    if (config.overrideMarks != null) {
      lines = memOverrideMarks(lines, config.overrideMarks, !singleRun);
    }

    if (props.configMode) {
      return (
        <Config
          {...props}
          data={savedData}
          lines={lines}
          lineCount={lineCount}
          defaultLegendTemplate={defaultLegendTemplate}
          setQueryStepRange={setQueryStepRange}
          setZoomTimestep={setZoomTimestep}
          zooming={zooming}
          setZooming={setZooming}
        />
      );
    } else {
      return (
        <Graph
          {...props}
          data={savedData}
          loading={props.loading}
          lines={lines}
          lineCount={lineCount}
          defaultLegendTemplate={defaultLegendTemplate}
          setQueryStepRange={setQueryStepRange}
          setZoomTimestep={setZoomTimestep}
          zooming={zooming}
          setZooming={setZooming}
        />
      );
    }
  },
  {id: 'PanelRunsLinePlot'}
);

// We don't want the panel infrastructure to do the query for us, since the query
// we make depends on internal state (the current zoom info). So we disable the
// outer query, and do our own query inside the panel.
const transformQuerySkip = (
  query: Query.Query,
  config: RunsLinePlotConfig
): RunsDataQuery => {
  const result = toRunsDataQuery(query);
  result.disabled = true;
  return result;
};

const useTableData = (pageQuery: Query.Query, config: RunsLinePlotConfig) => {
  const query = runsLinePlotTransformQuery(
    pageQuery,
    config as RunsLinePlotConfig,
    null,
    parseExpressions(config.expressions, config.xExpression)
  );

  return useSampleAndQueryToTable(query, pageQuery, config);
};

const configSpec = {
  chartTitle: {
    editor: 'string' as const,
    displayName: 'Chart title',
  },
  smoothingWeight: {
    editor: 'slider' as const,
    displayName: 'Smoothing',
    min: 0,
    max: 0.99,
    step: 0.01,
    default: 0,
  },
  ignoreOutliers: {
    editor: 'checkbox' as const,
    displayName: 'Hide outliers',
    default: false,
  },
  xLogScale: {
    editor: 'checkbox' as const,
    displayName: 'X log scale',
    default: false,
  },
  yLogScale: {
    editor: 'checkbox' as const,
    displayName: 'Y log scale',
    default: false,
  },
  limit: {
    editor: 'dropdown' as const,
    displayName: 'Runs limit',
    default: 10,
    options: [
      {text: '1 run', value: 1},
      {text: '2 runs', value: 2},
      {text: '3 runs', value: 3},
      {text: '4 runs', value: 4},
      {text: '5 runs', value: 5},
      {text: '6 runs', value: 6},
      {text: '7 runs', value: 7},
      {text: '8 runs', value: 8},
      {text: '9 runs', value: 9},
      {text: '10 runs', value: 10},
      {text: '20 runs', value: 20},
      {text: '30 runs', value: 30},
      {text: '50 runs', value: 50},
      {text: '100 runs', value: 100},
    ],
  },
  plotType: {
    editor: 'icon-menu' as const,
    displayName: 'Plot type',
    options: [
      {icon: 'standard-line-plot', value: 'line'},
      {icon: 'area-line-plot', value: 'stacked-area'},
      {icon: 'percent-line-plot', value: 'pct-area'},
    ],
    default: 'line',
  },
};

function getTitleFromConfig(config: RunsLinePlotConfig): string {
  return config.chartTitle || defaultTitle(config);
}

type Props = PanelConfigSpecToProps<typeof configSpec>;

export const Spec: Panels.PanelSpec<typeof PANEL_TYPE, RunsLinePlotConfig> = {
  type: PANEL_TYPE,
  Component: PanelRunsLinePlot,
  getTitleFromConfig,
  exportable: {
    image: true,
    csv: true,
    api: true,
  },
  configSpec,
  transformQuery: transformQuerySkip,
  useTableData,
  icon: 'panel-line-plot',
};
